import { List, Record } from 'immutable'
import { BrokeragePipeline, IBrokeragePipeline } from './BrokeragePipeline'
import { Contact, IContact } from './Contact'
import { DevelopmentClient, IDevelopmentClient } from './DevelopmentClient'
import { DevelopmentSite, IDevelopmentSite } from './DevelopmentSite'
import { FeedItem, IFeedItem } from './FeedItem'
import { IListing, Listing } from './Listing'
import { IProperty, Property } from './Property'
import { ITeam, Team } from './Team'

export interface IGlobalSearchResult {
  readonly brokeragePipelines: List<IBrokeragePipeline>
  readonly clients: List<IDevelopmentClient>
  readonly code: string
  readonly contacts: List<IContact>
  readonly feedItems: List<IFeedItem>
  readonly leads: List<IFeedItem>
  readonly listings: List<IListing>
  readonly properties: List<IProperty>
  readonly sites: List<IDevelopmentSite>
  readonly success: boolean
  readonly teams: List<ITeam>
  readonly totalResults: number
}

const GlobalSearchResultRecord = Record({
  brokeragePipelines: List<BrokeragePipeline>(),
  clients: List<DevelopmentClient>(),
  code: '',
  contacts: List<Contact>(),
  feedItems: List<FeedItem>(),
  leads: List<FeedItem>(),
  listings: List<Listing>(),
  properties: List<Property>(),
  sites: List<DevelopmentSite>(),
  success: false,
  teams: List<Team>(),
  totalResults: 0,
})

export class GlobalSearchResult
  extends GlobalSearchResultRecord
  implements IGlobalSearchResult
{
  public static fromJS(initialState?: Partial<IGlobalSearchResult>) {
    if (!initialState) {
      return new GlobalSearchResult()
    }

    const {
      brokeragePipelines,
      clients,
      contacts,
      feedItems,
      leads,
      listings,
      properties,
      sites,
      teams,
      ...rest
    } = initialState

    return new GlobalSearchResult({
      brokeragePipelines: Array.isArray(brokeragePipelines)
        ? List(brokeragePipelines.map((x) => BrokeragePipeline.fromJS(x)))
        : List<BrokeragePipeline>(),
      clients: Array.isArray(clients)
        ? List(clients.map((x) => DevelopmentClient.fromJS(x)))
        : List<DevelopmentClient>(),
      contacts: Array.isArray(contacts)
        ? List(contacts.map((x) => Contact.fromJS(x)))
        : List<Contact>(),
      feedItems: Array.isArray(feedItems)
        ? List(feedItems.map((x) => FeedItem.fromJS(x)))
        : List<FeedItem>(),
      leads: Array.isArray(leads)
        ? List(leads.map((x) => FeedItem.fromJS(x)))
        : List<FeedItem>(),
      listings: Array.isArray(listings)
        ? List(listings.map((x) => Listing.fromJS(x)))
        : List<Listing>(),
      properties: Array.isArray(properties)
        ? List(properties.map((x) => Property.fromJS(x)))
        : List<Property>(),
      sites: Array.isArray(sites)
        ? List(sites.map((x) => DevelopmentSite.fromJS(x)))
        : List<DevelopmentSite>(),
      teams: Array.isArray(teams)
        ? List(teams.map((x) => Team.fromJS(x)))
        : List<Team>(),
      ...rest,
    })
  }

  public readonly brokeragePipelines: List<BrokeragePipeline>
  public readonly clients: List<DevelopmentClient>
  public readonly code: string
  public readonly contacts: List<Contact>
  public readonly feedItems: List<FeedItem>
  public readonly leads: List<FeedItem>
  public readonly listings: List<Listing>
  public readonly properties: List<Property>
  public readonly sites: List<DevelopmentSite>
  public readonly success: boolean
  public readonly teams: List<Team>
  public readonly totalResults: number

  constructor(props?: Partial<GlobalSearchResult>) {
    props ? super(props) : super()
  }
}
